import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import MuiChip from '@mui/material/Chip';
import * as React from 'react';
import { IconButton } from '../button';
import { useMergeRefs, useOverflow } from '../hooks';
import { styled } from '../styled';
import { Tooltip } from '../tooltip';
import { Typography } from '../typography';
import { makeEnum, withVersion } from '../utils';
export * from './chip-group';
export * from './birthday-chip';
export * from './chip-variants';
const sizes = ['small', 'medium'];
export const ChipSize = makeEnum(...sizes);
const ChipImpl = React.forwardRef((props, ref) => {
    const { color = 'default', maxWidth = 150, size = 'small', label, onDelete, tooltipText, selected, ...muiProps } = props;
    const innerRef = useMergeRefs(ref);
    const labelRef = React.useRef(null);
    const { textOverflow } = useOverflow(labelRef, props.label);
    const isRemovable = Boolean(onDelete);
    const isSelectable = Boolean(muiProps.onClick) && !isRemovable;
    const resolvedDisabled = isSelectable ? muiProps.disabled : undefined;
    const isSelected = isSelectable && selected && !resolvedDisabled;
    const resolvedColor = isSelectable ? 'selectable' : color;
    const colorMain = getChipColor(resolvedColor, 'main');
    const colorHover = getChipColor(resolvedColor, 'hovered');
    const colorFocused = getChipColor(resolvedColor, 'focused');
    const colorActive = getChipColor(resolvedColor, 'active');
    const bgColor = isSelected ? colorActive : colorMain;
    const labelColor = getChipLabelColor(resolvedColor, isSelected);
    const hasSelectableInteraction = Boolean(!resolvedDisabled && isSelectable);
    const resolvedCursor = hasSelectableInteraction ? 'pointer' : 'default';
    const { onClick, ...forwardedMuiProps } = muiProps;
    return (_jsx(Tooltip, { title: tooltipText ?? props.label, bypass: tooltipText ? false : !textOverflow, children: _jsx(MuiChip, { ref: innerRef, label: label ? (isRemovable ? (_jsxs(RemovableLabel, { children: [typeof label === 'string' ? (_jsx(Label, { ref: labelRef, children: label })) : (label), _jsx(IconButton, { icon: "IconClose", size: "smaller", "aria-label": "delete", onClick: onDelete })] })) : typeof label === 'string' ? (_jsx(Label, { ref: labelRef, children: label })) : (label)) : undefined, "data-role": "chip", size: size, ...forwardedMuiProps, onClick: isSelectable ? onClick : undefined, disabled: resolvedDisabled, color: "default", sx: {
                backgroundColor: bgColor,
                color: labelColor,
                cursor: resolvedCursor,
                borderRadius: 1,
                '.MuiChip-label': label
                    ? {
                        boxSizing: 'content-box',
                        maxWidth
                    }
                    : {
                        padding: 0
                    },
                ...(hasSelectableInteraction && {
                    '&.MuiChip-clickable:hover': {
                        backgroundColor: isSelected ? bgColor : colorHover,
                        color: isSelected
                            ? labelColor
                            : getChipColor(resolvedColor, 'contrastText')
                    },
                    '&.MuiChip-clickable.Mui-focusVisible': {
                        backgroundColor: colorFocused
                    },
                    '&.MuiChip-clickable:active': {
                        backgroundColor: colorFocused
                    }
                }),
                ...muiProps.sx
            } }) }));
});
export const Chip = withVersion(ChipImpl, '1.2.0');
Chip.displayName = 'Chip';
const Label = React.forwardRef((props, ref) => {
    return (_jsx(Typography, { component: "span", ref: ref, overflow: "hidden", textOverflow: "ellipsis", display: "block", sx: {
            font: 'inherit',
            lineHeight: 'inherit',
            letterSpacing: 'inherit'
        }, children: props.children }));
});
const RemovableLabel = styled('span')({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    maxWidth: '100%'
});
function getChipColor(color, token) {
    return `components.chip.${color}.${token}`;
}
/** Returns the label ColorProp for the chip — switches to the active contrast token when selected. */
function getChipLabelColor(color, isSelected) {
    return isSelected
        ? getChipColor('selectable', 'contrast-text-active')
        : getChipColor(color, 'contrastText');
}
export const chipStyle = {
    MuiChip: {
        styleOverrides: {
            sizeSmall: ({ theme }) => ({
                height: 24,
                fontSize: theme.palette.components?.chip?.small?.fontSize,
                fontWeight: theme.palette.components?.chip?.small?.fontWeight,
                '& .MuiChip-icon': {
                    marginLeft: theme.spacing(0.5),
                    marginRight: theme.spacing(0.5)
                },
                '& .MuiChip-label': {
                    paddingLeft: theme.spacing(0.5),
                    paddingRight: theme.spacing(0.5)
                },
                '& .MuiChip-deleteIcon': {
                    padding: theme.spacing(0.375),
                    marginLeft: 0,
                    marginRight: theme.spacing(0.25),
                    color: theme.palette.action.active
                }
            }),
            sizeMedium: ({ theme }) => ({
                height: 30,
                fontSize: theme.palette.components?.chip?.medium?.fontSize,
                fontWeight: theme.palette.components?.chip?.medium?.fontWeight,
                '& .MuiChip-icon': {
                    marginLeft: theme.spacing(1),
                    marginRight: 0
                },
                '& .MuiChip-label': {
                    paddingLeft: theme.spacing(1),
                    paddingRight: theme.spacing(1)
                },
                '& .MuiChip-deleteIcon': {
                    marginLeft: theme.spacing(0.5),
                    marginRight: theme.spacing(0.5),
                    color: theme.palette.action.active
                }
            })
        }
    }
};
